import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import SessionContext from '../../../contexts/SessionContext';
import { fetchTopFlaggedVendorTypes } from '../../../hooks/reporting/useInvoicingSummary';
import { Box, Flex, Icon, Skeleton, Text } from '@chakra-ui/react';
import { TbBox, TbCurrencyDollar, TbPercentage } from 'react-icons/tb';
import type { WidgetConfig, WidgetSort } from './types';
import { widgetChartLayoutKey } from './types';
import { INVOICING_DEFAULT_LOCATION_LINE_CUTOFF } from './invoicingCatalog';
import InvoicingTimeseriesChart from '../components/InvoicingTimeseriesChart';
import type { ReportingComparison, InvoicingSummaryResponse, InvoicingTimeseriesResponse, InvoiceTypeKey, FlagTypeKey } from '../../../types/reporting';
import { buildInvoicingChartDrill, type InvoicingChartSegmentClick } from '../components/invoicingChartDrill';
import { InvoicingCompositionDonut } from '../components/InvoicingCompositionDonut';
import { TopFlaggedVendorTypes } from '../components/TopFlaggedVendorTypes';
import { INVOICING_COMPOSITION_METRICS, TOP_FLAGGED_VENDOR_TYPES_METRIC } from './invoicingCatalog';
import { getSummaryComparisonLabel } from '../shared/reportingComparisonLabels';
import {
    formatSummaryMetricChange,
    summaryChangeArrow,
    summaryChangeColor,
} from '../shared/summaryChangeDisplay';
import { calculateMetricChange } from '../shared/metricChange';
export interface InvoicingWidgetDataContext {
    summary?: InvoicingSummaryResponse;
    summaryLoading: boolean;
    summaryError: boolean;
    timeseries?: InvoicingTimeseriesResponse;
    timeseriesLoading: boolean;
    comparison?: ReportingComparison;
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    startDate?: string;
    endDate?: string;
    /** Selected invoice types. Empty or omitted means all types. */
    invoiceTypes?: InvoiceTypeKey[];
    /** Shop the reporting page is already scoped to. */
    locationId?: string;
    /** Tab location selection. Empty or omitted means every shop in scope. */
    locationIds?: string[];
    vendorNames?: string[];
    flagTypes?: FlagTypeKey[];
    flagMatch?: 'any' | 'exact';
}

/** Metrics where an increase is bad (shown red). */
const NEGATIVE_INVOICING_METRICS = new Set([
    'invoiceFlags',
    'invoiceFlagsValue',
    'creditsCount',
    'creditsValue',
    'totalCostVariance',
    'avgCostVariance',
    'avgDeliveryVarianceDays',
]);

const formatCurrency = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const formatCurrencyPrecise = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const formatPercent = (v: number) => `${v.toFixed(1)}%`;
const formatNumber = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 1 });
const formatDays = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(1)}d`;

function getFormatter(format: string, metric: string) {
    if (metric === 'avgDeliveryVarianceDays') return formatDays;
    if (metric === 'totalCostVariance' || metric === 'avgCostVariance') return formatCurrencyPrecise;
    if (format === 'currency') return formatCurrency;
    if (format === 'percent') return formatPercent;
    return formatNumber;
}
function getIcon(format: string) {
    if (format === 'currency') return TbCurrencyDollar;
    if (format === 'percent') return TbPercentage;
    return TbBox;
}

/** Roll up vendor-only summary metrics from byVendor rows (current or prior). */
function rollupVendorRows(
    rows: Array<{
        totalCostVariance: number;
        matchedPartCount: number;
        deliveryVarianceDaysSum: number;
        deliveryVariancePartCount: number;
    }> | undefined,
    metric: string,
): number | null | undefined {
    if (!rows || rows.length === 0) {
        if (metric === 'totalCostVariance' || metric === 'avgCostVariance') return 0;
        if (metric === 'avgDeliveryVarianceDays') return null;
        return undefined;
    }

    if (metric === 'totalCostVariance') {
        return rows.reduce((s, v) => s + (v.totalCostVariance ?? 0), 0);
    }
    if (metric === 'avgCostVariance') {
        const matched = rows.reduce((s, v) => s + (v.matchedPartCount ?? 0), 0);
        if (matched === 0) return 0;
        const sum = rows.reduce((s, v) => s + (v.totalCostVariance ?? 0), 0);
        return sum / matched;
    }
    if (metric === 'avgDeliveryVarianceDays') {
        const partCount = rows.reduce((s, v) => s + (v.deliveryVariancePartCount ?? 0), 0);
        if (partCount === 0) return null;
        const daysSum = rows.reduce((s, v) => s + (v.deliveryVarianceDaysSum ?? 0), 0);
        return daysSum / partCount;
    }
    return undefined;
}

/** Resolve a summary metric, rolling up from byVendor when not present top-level. */
function resolveSummaryMetric(summary: InvoicingSummaryResponse, metric: string): number | undefined | null {
    const direct = (summary as unknown as Record<string, unknown>)[metric];
    if (typeof direct === 'number') return direct;
    return rollupVendorRows(summary.byVendor, metric);
}

const VARIANCE_SUMMARY_METRICS = new Set([
    'totalCostVariance',
    'avgCostVariance',
    'avgDeliveryVarianceDays',
]);

function resolveSummaryChange(
    summary: InvoicingSummaryResponse | undefined,
    metric: string,
): number | undefined {
    if (!summary) return undefined;
    const fromApi = summary.change?.[metric];
    if (typeof fromApi === 'number') return fromApi;
    if (!VARIANCE_SUMMARY_METRICS.has(metric) || !summary.prior?.byVendor) return undefined;

    const current = rollupVendorRows(summary.byVendor, metric);
    const prior = rollupVendorRows(summary.prior.byVendor, metric);
    if (current === null || prior === null || current === undefined || prior === undefined) {
        return undefined;
    }
    return calculateMetricChange(current, prior, metric);
}

interface Props {
    config: WidgetConfig;
    data: InvoicingWidgetDataContext;
    onSortChange?: (sort: WidgetSort) => void;
}

const InvoicingWidgetRenderer = ({ config, data, onSortChange }: Props) => {
    const { preset } = data;
    const chartHeight = '100%';
    const navigate = useNavigate();
    const sessionContext = useContext(SessionContext);
    const loadMoreFlaggedVendorTypes = useCallback((offset: number) => {
        return fetchTopFlaggedVendorTypes(sessionContext?.sessionId ?? '', {
            startDate: data.startDate ?? '',
            endDate: data.endDate ?? '',
            locationId: data.locationIds?.length ? undefined : data.locationId,
            locationIds: data.locationIds?.length ? data.locationIds : undefined,
            preset: data.preset,
            vendorNames: data.vendorNames?.length ? data.vendorNames : undefined,
            invoiceTypes: data.invoiceTypes?.length ? data.invoiceTypes : undefined,
            flagTypes: data.flagTypes?.length ? data.flagTypes : undefined,
            flagMatch: data.flagTypes?.length && data.flagMatch === 'exact' ? 'exact' : undefined,
        }, offset);
    }, [
        sessionContext?.sessionId,
        data.startDate,
        data.endDate,
        data.locationId,
        data.locationIds,
        data.preset,
        data.vendorNames,
        data.invoiceTypes,
        data.flagTypes,
        data.flagMatch,
    ]);
    const flaggedVendorTypesResetKey = [
        data.startDate,
        data.endDate,
        data.locationId,
        data.locationIds?.join(','),
        data.vendorNames?.join(','),
        data.invoiceTypes?.join(','),
        data.flagTypes?.join(','),
        data.flagMatch,
        data.summary?.topFlaggedVendorTypes
            ?.map((row) => `${row.vendorName}:${row.invoiceType}:${row.invoiceCount}:${row.flagRate}`)
            .join('|'),
    ].join('|');
    const openInvoiceFeed = useCallback((segment: InvoicingChartSegmentClick) => {
        if (!data.startDate || !data.endDate) return;
        const view = buildInvoicingChartDrill({
            startDate: data.startDate,
            endDate: data.endDate,
            granularity: data.granularity,
            locationId: data.locationId,
            locationIds: data.locationIds,
            vendorNames: data.vendorNames,
            invoiceTypes: data.invoiceTypes,
            flagTypes: data.flagTypes,
            flagMatch: data.flagMatch,
            segment,
        });
        if (!view) return;
        navigate('/invoices', { state: { invoicesViewState: view } });
    }, [
        data.startDate,
        data.endDate,
        data.granularity,
        data.locationId,
        data.locationIds,
        data.vendorNames,
        data.invoiceTypes,
        data.flagTypes,
        data.flagMatch,
        navigate,
    ]);

    if (config.widgetType === 'keyMetric') {
        const isLoading = data.summaryLoading;
        const summaryData = data.summary;
        const value = summaryData ? resolveSummaryMetric(summaryData, config.metric) : undefined;
        const fmt = getFormatter(config.format, config.metric);
        const IconComp = getIcon(config.format);

        const comparisonEnabled = config.comparison !== false;
        const changeDisplay = formatSummaryMetricChange(
            resolveSummaryChange(summaryData, config.metric),
            {
                suffix: config.format === 'percent' ? 'pp' : '%',
                isNegativeMetric: NEGATIVE_INVOICING_METRICS.has(config.metric),
                comparisonEnabled,
            },
        );
        const changePeriod = getSummaryComparisonLabel(preset);

        return (
            <Flex align="center">
                <Flex alignItems="center" justifyContent="center" w={9} h={9} borderRadius="full" bg="gray.100" mr={3} flexShrink={0}>
                    <Icon as={IconComp as React.ElementType} w={5} h={5} color="#0071E3" />
                </Flex>
                <Box flex="1" minW={0}>
                    <Skeleton isLoaded={!isLoading} h={isLoading ? '28px' : 'auto'} w={isLoading ? '100px' : 'auto'} borderRadius="md">
                        <Flex alignItems="baseline" gap={2}>
                            <Text fontSize="xl" fontWeight="bold" color="gray.900" lineHeight="tight">
                                {data.summaryError
                                    ? '—'
                                    : value === null
                                        ? '—'
                                        : value !== undefined
                                            ? fmt(config.metric === 'creditsValue' ? Math.abs(value) : value)
                                            : '—'}
                            </Text>
                            {!isLoading && changeDisplay && (
                                <Text fontSize="xs" fontWeight="medium" color={summaryChangeColor(changeDisplay.tone)}>
                                    {summaryChangeArrow(changeDisplay.tone)}{changeDisplay.text}
                                    {changePeriod && (
                                        <Text as="span" color="gray.400" fontWeight="normal" ml={1}>
                                            {changePeriod}
                                        </Text>
                                    )}
                                </Text>
                            )}
                        </Flex>
                    </Skeleton>
                </Box>
            </Flex>
        );
    }

    if (config.widgetType === 'timeSeries' && config.metric === TOP_FLAGGED_VENDOR_TYPES_METRIC) {
        return (
            <Box h="100%" w="100%" minH={0} minW={0}>
                {data.summaryLoading && !data.summary ? (
                    <Skeleton h="100%" borderRadius="md" />
                ) : data.summaryError ? (
                    <Text color="gray.400" fontSize="sm">Couldn't load this list.</Text>
                ) : (
                    <TopFlaggedVendorTypes
                        rows={data.summary?.topFlaggedVendorTypes}
                        hasMore={data.summary?.topFlaggedVendorTypesHasMore}
                        resetKey={flaggedVendorTypesResetKey}
                        onLoadMore={loadMoreFlaggedVendorTypes}
                        onRowClick={data.startDate && data.endDate
                            ? (row) => openInvoiceFeed({
                                seriesKey: `vendtype:${row.invoiceType}`,
                                seriesLabel: row.vendorName,
                            })
                            : undefined}
                    />
                )}
            </Box>
        );
    }

    if (config.widgetType === 'timeSeries' && INVOICING_COMPOSITION_METRICS.has(config.metric)) {
        return (
            <Box h="100%" w="100%" minH={0} minW={0}>
                {data.summaryLoading && !data.summary ? (
                    <Skeleton h="100%" borderRadius="md" />
                ) : data.summaryError ? (
                    <Text color="gray.400" fontSize="sm">Couldn't load this chart.</Text>
                ) : (
                    <InvoicingCompositionDonut
                        metric={config.metric as 'flaggedFaultBreakdown' | 'stripeBillingMix'}
                        data={data.summary?.composition}
                        onSliceClick={data.startDate && data.endDate
                            ? (seriesKey, seriesLabel) => openInvoiceFeed({ seriesKey, seriesLabel })
                            : undefined}
                    />
                )}
            </Box>
        );
    }

    if (config.widgetType === 'timeSeries') {
        return (
            <Box h="100%" w="100%" minH={0} minW={0}>
                <InvoicingTimeseriesChart
                    key={widgetChartLayoutKey(config)}
                    data={data.timeseries}
                    isLoading={data.timeseriesLoading}
                    height={chartHeight}
                    metric={config.metric}
                    domain={config.domain}
                    chartStyle={config.chartStyle ?? 'bars'}
                    comparison={data.comparison}
                    preset={data.preset}
                    granularity={data.granularity}
                    periodStartDate={data.startDate}
                    periodEndDate={data.endDate}
                    showComparisonBars={Boolean(config.showComparisonBars)}
                    initialSortOrder={config.sort ?? 'desc'}
                    onSortOrderChange={onSortChange}
                    locationCutoff={config.cutoff ?? INVOICING_DEFAULT_LOCATION_LINE_CUTOFF}
                    locationShowAll={config.showAllRows ?? false}
                    invoiceTypes={data.invoiceTypes}
                    onSegmentClick={data.startDate && data.endDate ? openInvoiceFeed : undefined}
                />
            </Box>
        );
    }

    return <Text color="gray.400" fontSize="sm">Unsupported widget type</Text>;
};

export default InvoicingWidgetRenderer;
